import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ReactComponent as InvestorIcon } from "../../assets/images/investorBannerIcon.svg";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

function InvestorBanner() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [openSignup, setOpenSignup] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [signupData, setSignupData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    pincode: "",
    password: "",
    confirmPassword: "",
  });

  const handleSignupOpen = () => setOpenSignup(true);
  const handleInvestNow = () => navigate("/project");
  const handleSignupClose = () => {
    setOpenSignup(false);
    setFormErrors({});
    setPasswordError(false);
  };

  const validateForm = () => {
    const errors = {};
    if (!signupData.name.trim()) errors.name = "Name is required";
    if (!signupData.email) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email))
      errors.email = "Invalid email format";

    if (!signupData.phone) errors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(signupData.phone))
      errors.phone = "Phone must be 10 digits";

    if (!signupData.state.trim()) errors.state = "State is required";
    if (!signupData.city.trim()) errors.city = "City is required";

    if (!signupData.pincode) errors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(signupData.pincode))
      errors.pincode = "Pincode must be 6 digits";

    if (!signupData.password) errors.password = "Password is required";
    if (!signupData.confirmPassword)
      errors.confirmPassword = "Confirm password is required";
    else if (signupData.password !== signupData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      setPasswordError(true);
    } else {
      setPasswordError(false);
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));

    if (
      (name === "password" || name === "confirmPassword") &&
      signupData.confirmPassword
    ) {
      setPasswordError(
        name === "confirmPassword"
          ? value !== signupData.password
          : signupData.confirmPassword !== value
      );
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setApiError("");
    setLoading(true);

    try {
      const response = await fetch(
        "http://43.204.235.152:8000/api/users/signup",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Name: signupData.name,
            Email: signupData.email,
            Mobile_Number: signupData.phone,
            State: signupData.state,
            City: signupData.city,
            Pincode: signupData.pincode,
            Password: signupData.confirmPassword,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Signup failed");
      }

      toast.success("Signup successful!");
      navigate("/disclaimer", { state: { signupData } });
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        background:
          "linear-gradient(90deg, rgba(235, 185, 18, 1) 20%, rgba(235,185,18,0.2) 100%)",
      }}
    >
      <Box
        sx={{
          m: "auto",
          width: { xs: "98%", md: "85%" },
          display: "flex",
          p: { xs: 1, md: 2 },
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: { xs: "83%", md: "70%" },
            display: "flex",
            gap: { xs: "8px", md: "16px" },
            alignItems: "center",
          }}
        >
          <InvestorIcon />
          <Typography
            sx={{
              fontSize: { xs: "16px", md: "40px" },
              fontWeight: 400,
              color: "#fff",
            }}
          >
            Start Your Farming Journey
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: "27%", md: "30%" },
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={handleInvestNow}
            sx={{
              borderRadius: { xs: "20px", md: "30px" },
              p: 1,
              width: { xs: "100px", md: "290px" },
              height: "50px",
              backgroundColor: "#23766a",
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "#EBB912",
                color: "#23766a",
              },
            }}
          >
             Start Now
          </Button>
        </Box>
      </Box>

      <Dialog
        open={openSignup}
        onClose={handleSignupClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            backgroundColor: "#23766a",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Create Your Account
          <IconButton onClick={handleSignupClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {[
              { name: "name", label: "Full Name", md: 12 },
              { name: "email", label: "Email Address", md: 6, type: "email" },
              { name: "phone", label: "Phone Number", md: 6 },
              { name: "state", label: "State", md: 4 },
              { name: "city", label: "City", md: 4 },
              { name: "pincode", label: "Pincode", md: 4 },
              { name: "password", label: "Password", md: 6, type: "password" },
              {
                name: "confirmPassword",
                label: "Confirm Password",
                md: 6,
                type: "password",
              },
            ].map((field) => (
              <Grid key={field.name} item xs={12} md={field.md}>
                <TextField
                  fullWidth
                  margin="normal"
                  name={field.name}
                  label={field.label}
                  type={field.type || "text"}
                  value={signupData[field.name]}
                  onChange={handleChange}
                  error={
                    !!formErrors[field.name] ||
                    (field.name === "confirmPassword" && passwordError)
                  }
                  helperText={
                    formErrors[field.name] ||
                    (field.name === "confirmPassword" && passwordError
                      ? "Passwords do not match"
                      : "")
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#1a5c54",
                      },
                      "&:hover fieldset": {
                        borderColor: "#1a5c54",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#1a5c54",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#1a5c54",
                      "&.Mui-focused": {
                        color: "#1a5c54",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      padding: { xs: "10.5px 14px", sm: "12.5px 14px" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      transform: {
                        xs: "translate(14px, 12px) scale(1)",
                        sm: "translate(14px, 16px) scale(1)",
                      },
                    },
                    "& .MuiInputLabel-shrink": {
                      transform: {
                        xs: "translate(14px, -6px) scale(0.75)",
                        sm: "translate(14px, -9px) scale(0.75)",
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      lineHeight: { xs: "1.2", sm: "1.4375em" },
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
          {apiError && (
            <Typography
              color="error"
              sx={{ mt: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              {apiError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleSignupClose}
            disabled={loading}
            variant="outlined"
            sx={{
              mr: 2,
              color: "#1a5c54",
              borderColor: "#1a5c54",
              "&:hover": {
                borderColor: "#1a5c54",
                backgroundColor: "rgba(26, 92, 84, 0.04)", // Optional: subtle hover effect
              },
              "&.Mui-disabled": {
                color: "rgba(26, 92, 84, 0.38)",
                borderColor: "rgba(26, 92, 84, 0.38)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            sx={{
              backgroundColor: "#23766a",
              "&:hover": { backgroundColor: "#1a5c54" },
              width: "30vh",
              height: "5vh",
              position: "relative", // Ensures the spinner is positioned inside the button
              padding: "10px 20px", // Add padding to make space for the spinner
            }}
          >
            {loading ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "60%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress size={24} sx={{ color: "white" }} />
              </Box>
            ) : (
              "Sign Up & Continue"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvestorBanner;
